import { useState, useEffect, useRef } from "react";
import { Menu, MoreVertical, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import defaultAvatar from "../assets/user.png";

const Header = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [avatarURL, setAvatarURL] = useState(defaultAvatar);
  const [userProfile, setUserProfile] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMore, setShowMore] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const moreDropdownRef = useRef(null);
  const navigate = useNavigate();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
        setShowMore(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      setIsLoggedIn(true);
      const profile = JSON.parse(localStorage.getItem("userProfile") || "{}");
      setUserProfile(profile);
      if (profile?.avatarURL) {
        setAvatarURL(profile.avatarURL);
      }
    } else {
      setIsLoggedIn(false);
    }
  }, []);

  const handleSignInClick = () => navigate("/signup");

  const handleLogout = () => {
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("userProfile");
    setIsLoggedIn(false);
    setUserProfile(null);
    navigate("/");
  };

  const navigationLinks = [
    { href: "/gallery", label: "Gallery" },
    { href: "/events", label: "Events" },
    { href: "/projects", label: "Projects" },
    { href: "/opportunities", label: "Opportunities" },
    { href: "/about", label: "About" },
    { href: "/contact", label: "Contact" },
  ];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${isScrolled
          ? 'bg-slate-900/95 backdrop-blur-md border-b border-white/10'
          : 'bg-black/20 backdrop-blur-sm'
        }`}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Mobile menu button */}
            {isMobile && (
              <button
                className="rounded-md p-2 text-gray-300 hover:bg-white/10 hover:text-white transition-colors md:hidden"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            )}

            {/* Brand Text */}
            <div className="flex items-center">
              <h1 
                className="brand-text text-3xl md:text-4xl cursor-pointer transition-transform hover:scale-105 my-10 [word-spacing:8px]"
                onClick={() => (window.location.href = "/")}
              >
                aeronauts
              </h1>
            </div>



            {/* Desktop Navigation */}
            <nav className="hidden md:flex md:space-x-6 lg:space-x-8">
              {navigationLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-gray-300 hover:text-white transition-colors duration-200 font-medium text-sm lg:text-base"
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right side actions */}
            <div className="flex items-center space-x-3 md:space-x-4">
              {isLoggedIn ? (
                <img
                  src={avatarURL}
                  alt="User Profile"
                  className="h-6 w-6 rounded-full cursor-pointer ring-1 ring-blue-500 hover:ring-blue-400 transition-all"
                  onClick={() => navigate("/dashboard")}
                />
              ) : (
                <button
                  className="rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 md:px-6 text-sm font-semibold text-white shadow-lg transition-all hover:scale-105 hover:shadow-blue-500/25"
                  onClick={handleSignInClick}
                >
                  Sign in
                </button>
              )}

              <div className="relative" ref={moreDropdownRef}>
                <button
                  className="rounded-md p-2 text-gray-300 hover:bg-white/10 hover:text-white transition-colors"
                  onClick={() => setShowMore(!showMore)}
                >
                  <MoreVertical size={20} />
                </button>

                {showMore && (
                  <div className="absolute right-0 top-12 w-48 rounded-lg border border-white/10 bg-slate-800/95 backdrop-blur-md p-4 shadow-xl z-50">
                    <p className="text-sm text-gray-300">
                      Developed by{" "}
                      <span
                        className="cursor-pointer text-blue-400 hover:text-blue-300 transition-colors"
                        onClick={() => window.open("https://github.com/Vishal8700", "_blank")}
                      >
                        Gitalien
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sliding Menu */}
      {isMobile && (
        <>
          <div className={`fixed inset-y-0 left-0 z-[110] w-64 transform bg-slate-900/95 backdrop-blur-md transition-transform duration-300 ease-in-out ${isMenuOpen ? "translate-x-0" : "-translate-x-full"
            }`}>
            <div className="flex h-full flex-col">
              <div className="flex h-16 items-center justify-between px-4 border-b border-white/10">
                <h1 className="brand-text text-xl">aeronauts</h1>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="rounded-md p-2 text-gray-300 hover:bg-white/10 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <nav className="flex-1 px-4 py-6 space-y-2">
                {navigationLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block rounded-md px-3 py-2 text-gray-300 hover:bg-white/10 hover:text-white transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>

              {isLoggedIn && (
                <div className="border-t border-white/10 p-4">
                  <div className="flex items-center space-x-3">
                    <img
                      src={avatarURL}
                      alt="User Profile"
                      className="h-8 w-8 rounded-full ring-2 ring-blue-500 cursor-pointer"
                      onClick={() => {
                        navigate("/dashboard");
                        setIsMenuOpen(false);
                      }}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{userProfile?.name || "My Profile"}</p>
                      <button
                        onClick={handleLogout}
                        className="text-xs text-gray-400 hover:text-white transition-colors"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {isMenuOpen && (
            <div
              className="fixed inset-0 z-[105] bg-black/50 backdrop-blur-sm"
              onClick={() => setIsMenuOpen(false)}
            />
          )}
        </>
      )}
    </>
  );
};

export default Header;
